import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { BasicScrollView } from "@/components/BasicScrollView";
import { BasicWrapperView } from "@/components/BasicWrapperView";
import { FlatList, Keyboard, PixelRatio, Platform, Pressable, RefreshControl, ScrollView, StyleSheet, useWindowDimensions } from 'react-native';
import { View } from '@/components/Themed';
import { Image } from 'expo-image';
import Head from 'expo-router/head';
import * as AppUtils from '@/utilities/AppUtils';
import { CustomText, CustomTextStyles } from '@/components/CustomText';
import { CommunityMembership } from "@/types/community_membership";
import { Redirect, RelativePathString, useFocusEffect, useRouter, Link } from 'expo-router';
import * as AppUtilities from '@/utilities/AppUtilities';
import * as AppConstants from '@/utilities/AppConstants';
import * as AppConsts from '@/utilities/AppConsts';
import { Button, CloseIcon, TextInput } from 'react95-native-rabbl';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { AppText, AppTextStyles } from '@/components/AppText';
import { setData, removeSearchResult, appendSearchResults, setSearchString } from '@/state/user';
//import YoutubePlayer from "react-native-youtube-iframe";
import { Video, IVideo } from '@/components/Video';
import { AppContext } from '@/components/AppContext';
import { useHeaderHeight } from "expo-router/react-navigation";
import { debounce } from 'lodash';
import { useBottomTabBarHeight } from "expo-router/js-tabs";
import { CustomDivider } from '@/components/CustomDivider';
import { Meta } from '@/components/Meta';
// @ts-ignore
import { RefreshControl as WebRefreshControl } from 'react-native-web-refresh-control';

const RefreshComponent = Platform.OS === 'web' ? WebRefreshControl : RefreshControl;

const BORDER_WIDTH = 0;
const DEBUG_BACKGROUND = false;

const PROFILE_IMAGE_WIDTH = AppConsts.BASIC_MINIMUM_WIDTH;
const PROFILE_IMAGE_HEIGHT = AppConsts.BASIC_MINIMUM_WIDTH;
const DECISION_IMAGE_WIDTH = 48;
const DECISION_IMAGE_HEIGHT = 48;
const TEXT_HEADER_HEIGHT = 30;

// Text under the image such as age, location, etc.
const TEXT_FOOTER_PADDING_TOP = 10;
const TEXT_FOOTER_GAP = 5;
// PixelRatio.getPixelSizeForLayoutSize(AppConsts.DEFAULT_FONT_SIZE)
const TEXT_FOOTER_HEIGHT = (25 * 3) + TEXT_FOOTER_PADDING_TOP + TEXT_FOOTER_GAP;
const ITEM_WRAPPER_COMPONENTS_GAP = 10;
const ITEM_COMPONENTS = 4;
const ROW_SEPARATOR_HEIGHT = 30;
const ITEM_HORIZONTAL_PADDING = 30;
const ITEM_WRAPPER_VERTICAL_PADDING = 10;

// Search & filter button (excluding topmost header for the navbar, tab bar height, etc.)
const HEADER_HEIGHT = 70;

const NON_IMAGE_HEIGHT =
        TEXT_HEADER_HEIGHT +
        DECISION_IMAGE_HEIGHT +
        TEXT_FOOTER_HEIGHT +
        (ITEM_WRAPPER_COMPONENTS_GAP * (ITEM_COMPONENTS - 1)) +
        (2 * ITEM_WRAPPER_VERTICAL_PADDING)
        ;

const ITEM_WIDTH = PROFILE_IMAGE_WIDTH;

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    padding: 20,
    alignItems: 'center',
    backgroundColor: 'transparent',
    gap: 20,
  },
  noResultsWrapper: {
    flex: 1,
    padding: 20,
    alignItems: 'center',
    backgroundColor: 'transparent',
    gap: 20,
  },
  container: {
    backgroundColor: 'transparent',
    flex: 1,
  },
  flex1: {
    flex: 1,
  },
  searchInput: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  createButton: {
    paddingTop: 20,
    alignItems: 'center',
  },
  padding3: {
    padding: 3,
    backgroundColor: 'transparent',
  },
  padding5: {
    padding: 5,
    backgroundColor: 'transparent',
  },
  videoWrapper: {
    alignItems: 'center',
    backgroundColor: 'transparent',
    paddingTop: 20,
  },
  transparent: {
    backgroundColor: 'transparent',
  },
  itemDecisionsWrapper: {
    flexDirection: 'row',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    //width: '100%',
    gap: 40,
    borderWidth: BORDER_WIDTH,
    borderColor: "purple",
    //height: DECISION_IMAGE_HEIGHT,
    alignItems: "center",
  },
  decisionImage: {
    width: DECISION_IMAGE_WIDTH,
    height: DECISION_IMAGE_HEIGHT,
  },
  itemWrapper: {
    alignItems: 'center',
    marginHorizontal: ITEM_HORIZONTAL_PADDING,
    paddingVertical: ITEM_WRAPPER_VERTICAL_PADDING,
    backgroundColor: DEBUG_BACKGROUND ? 'white' : 'transparent',
    gap: ITEM_WRAPPER_COMPONENTS_GAP,
    borderWidth: BORDER_WIDTH,
    borderColor: "red",
  },
  itemHeaderWrapper: {
    //height: TEXT_HEADER_HEIGHT,
    backgroundColor: DEBUG_BACKGROUND ? 'lightgreen' : 'transparent',
    borderWidth: BORDER_WIDTH,
    borderColor: "yellow",
  },
  itemImageWrapper: {
    backgroundColor: DEBUG_BACKGROUND ? 'red' : 'transparent',
  },
  itemFooterWrapper: {
    backgroundColor: 'transparent',
    //height: TEXT_FOOTER_HEIGHT,
    paddingTop: TEXT_FOOTER_PADDING_TOP,
    gap: TEXT_FOOTER_GAP,
    alignItems: 'center',
    borderWidth: BORDER_WIDTH,
  },
  rowSeparator: {
    height: ROW_SEPARATOR_HEIGHT,
    backgroundColor: 'transparent',
  },
  listContentContainerStyle: {
    alignItems: 'center',
  },
});

function getFilterText(communityMembership: CommunityMembership): string {
  let result = '';
  
  if (communityMembership.user_info.desired_age_max == AppConsts.MAX_AGE) {
    result += "Age ≥ " + communityMembership.user_info.desired_age_min;
  } else {
    result += "Age: " + communityMembership.user_info.desired_age_min + " - " + communityMembership.user_info.desired_age_max;
  }

  if (communityMembership.user_info.desired_height_min && communityMembership.user_info.desired_height_min > 0 &&
      communityMembership.user_info.desired_height_max && communityMembership.user_info.desired_height_max > 0 &&
      (communityMembership.user_info.desired_height_max != AppConsts.MAX_HEIGHT_INCHES || communityMembership.user_info.desired_height_min != AppConsts.MIN_HEIGHT_INCHES)
  ) {
    result += ", " + AppUtils.getHeightHumanReadable(communityMembership.user_info.desired_height_min) + " - " +
                             AppUtils.getHeightHumanReadable(communityMembership.user_info.desired_height_max);
    //result += ", Height: " + AppUtils.getHeightHumanReadable(communityMembership.user_info.desired_height_min) + " - " +
    //                         AppUtils.getHeightHumanReadable(communityMembership.user_info.desired_height_max);
  }
  if (communityMembership.user_info.desired_max_distance_miles &&
      communityMembership.user_info.desired_max_distance_miles > 0 &&
      communityMembership.user_info.desired_max_distance_miles < 25000
  ) {
    //result += ", Distance ≤ " + communityMembership.user_info.desired_max_distance_miles + " mile";
    result += ", ≤ " + communityMembership.user_info.desired_max_distance_miles + " mile";
    if (communityMembership.user_info.desired_max_distance_miles > 1) {
      result += "s";
    }
  }
  return result;
}

interface ItemContainerProps {
  member: CommunityMembership,
  itemHeight: number,
  imageHeight: number,
  imageWidth: number,
  referenceDomain: string,
  referenceMaxCols: number,
}

const ItemContainer: React.FC<ItemContainerProps> = ({
  member,
  itemHeight,
  imageWidth,
  imageHeight,
  referenceDomain,
  referenceMaxCols,
}) => {

  const domain = useSelector((state: RootState) => state.user.domain);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const dispatch = useDispatch();
  const router = useRouter();
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);

  const decide = useCallback(async (like: boolean) => {
    if (communityMembership.hidden) {
      AppUtils.showErrorToast("Your profile is currently hidden so you can't decide on profiles.", () => router.navigate("/settings"), true, "Settings");
      return;
    }

    try {
      showSpinner();
      AppUtilities.info("ItemContainer.decide started");
      const response = await AppUtilities.fetchAuthorized(
        "ItemContainer.decide",
        "/rabbl/api/decide",
        "POST",
        {
          domain: domain,
          id: member.uniqueid,
          like: like,
        }
      );
      AppUtilities.info("ItemContainer.decide finished:");
      AppUtilities.dir((response as any).body);
      dispatch(removeSearchResult(member));
      dispatch(setData(response));
      const new_match: boolean = ((response as any).body as any).new_match;
      if (new_match) {
        AppUtils.showPressableToast(
          "You've got a match!",
          () => {
            router.navigate('/chats');
          },
          "Show",
        );
      }
    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => decide(like), true);
      AppUtilities.handleException(e, "ItemContainer.decide");
    } finally {
      hideSpinner();
    }
  }, [domain, communityMembership]);

  const openmatch = useCallback(async () => {
    if (communityMembership.hidden) {
      AppUtils.showErrorToast("Your profile is currently hidden so you can't start new chats.", () => router.navigate("/settings"), true, "Settings");
      return;
    }

    try {
      showSpinner();
      const response = await AppUtilities.fetchAuthorized(
        "ItemContainer.openmatch",
        "/rabbl/api/openmatch",
        "POST",
        {
          domain: domain,
          id: member.uniqueid,
        }
      );
      AppUtilities.dir((response as any).body);
      const new_match_id: number = ((response as any).body as any).new_match_id;
      dispatch(removeSearchResult(member));
      dispatch(setData(response));
      router.navigate(("/chat/" + new_match_id) as RelativePathString);
    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => openmatch(), true);
      AppUtilities.handleException(e, "ItemContainer.openmatch");
    } finally {
      hideSpinner();
    }
  }, [domain, communityMembership]);

  return (
    <View style={styles.itemWrapper}>
      
      <View style={[styles.itemHeaderWrapper, { width: imageWidth, alignItems: "center" }]}>
        <Link href={('/profile/' + member.uniqueid) as RelativePathString} asChild={AppUtilities.isWeb()}>
          <CustomText ellipsizeMode='tail' numberOfLines={1}>
            {member.profile.name}
          </CustomText>
        </Link>
      </View>
      
      <View style={styles.itemImageWrapper}>
        <Link href={('/profile/' + member.uniqueid) as RelativePathString} asChild={AppUtilities.isWeb()}>
          <Image
            alt={member.profile.name}
            contentFit='contain'
            style={{
              width: imageWidth,
              height: imageHeight,
              borderWidth: BORDER_WIDTH,
            }}
            source={{
              uri: member.profile.visuals && member.profile.visuals.length > 0 ?
                        AppUtils.createBackendLink('/rabbl/api/visual?thumbnail=yes&uniqueid=' + member.profile.visuals[0].uniqueid)
                      :
                        require('@/assets/images/butterfly325x325.png'),
            }}
          />
        </Link>
      </View>

      <View style={styles.itemDecisionsWrapper}>
        {
          member.user_info.pref_allow_unmatched_messages && 
            <Pressable
              style={({ pressed }) => [
                {
                  opacity: pressed ? 0.5 : 1,
                },
              ]}
              onPress={openmatch}
            >
              <Image style={styles.decisionImage} source={require('@/assets/images/PixelarticonsMessageText48x48.png')} />
            </Pressable>
        }
        <Pressable
          style={({ pressed }) => [
            {
              opacity: pressed ? 0.5 : 1,
            },
          ]}
          onPress={() => decide(false)}
        >
          <Image style={styles.decisionImage} source={require('@/assets/images/PixelarticonsClose48x48-black.png')} />
        </Pressable>
        <Pressable
          style={({ pressed }) => [
            {
              opacity: pressed ? 0.5 : 1,
            },
          ]}
          onPress={() => decide(true)}
        >
          <Image style={styles.decisionImage} source={require('@/assets/images/heart48x48.png')} />
        </Pressable>
      </View>

      <View style={[styles.itemFooterWrapper, { width: imageWidth, alignItems: "center" }]}>
        <Link href={('/profile/' + member.uniqueid) as RelativePathString} asChild={AppUtilities.isWeb()}>
          <AppText ellipsizeMode='tail' numberOfLines={1}>
            {AppUtils.getMemberAge(member)} - {AppUtils.getMemberShortLocation(member.user_info)}
          </AppText>
        </Link>
        <Link href={('/profile/' + member.uniqueid) as RelativePathString} asChild={AppUtilities.isWeb()}>
          <AppText style={AppTextStyles.small} ellipsizeMode='tail' numberOfLines={1}>
            {AppUtils.getDistanceDisplay(member.reference_distance_miles ?? 0)} miles
          </AppText>
        </Link>
        <Link href={('/profile/' + member.uniqueid) as RelativePathString} asChild={AppUtilities.isWeb()}>
          <AppText style={AppTextStyles.tinyItalics} ellipsizeMode='tail' numberOfLines={1}>
            {
              referenceDomain != member.community?.domain ?
                  "Collab w/ " + member.community?.name
                :
                  "\u00A0"
            }
          </AppText>
        </Link>
      </View>
      { referenceMaxCols == 1 && (
        <CustomDivider />
      )}
    </View>
  );
};

function NoResults() : React.ReactNode {
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const video = useRef<IVideo>(null);

  useFocusEffect(
    useCallback(() => {
      return () => {
        video.current?.pause();
      };
    }, [])
  );

  return (
    <>
      <View style={styles.noResultsWrapper}>
        <CustomText>No more results</CustomText>
        {
          communityMembership.user_info.pref_notify_daily_email ?
              <AppText>No more results match <Link href="/filter" style={CustomTextStyles.link}>your filter</Link>. We'll email you daily if more show up. Check your spam/promotions/junk folders.</AppText>
            :
              communityMembership.user_info.pref_notify_weekly_email ?
                  <AppText>No more results match <Link href="/filter" style={CustomTextStyles.link}>your filter</Link>. We'll email you weekly if more show up. Check your spam/promotions/junk folders.</AppText>
                :
                  <AppText>No more results match <Link href="/filter" style={CustomTextStyles.link}>your filter</Link>. Consider enabling the <Link href="/preferences?edit=y" style={CustomTextStyles.link}>setting</Link> to get weekly emails.</AppText>
        }
      </View>
      <View style={styles.videoWrapper}>
        <Video
          source={"/videos/5wI1gOKm70k.mp4"}
          width={AppConsts.DEFAULT_VIDEO_WIDTH}
          height={127}
          autoplay={false}
          ref={video}
        />
      </View>
    </>
  );
}

function ResultsHeader() : React.ReactNode {
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const dispatch = useDispatch();
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const domain = useSelector((state: RootState) => state.user.domain);
  const search_string = useSelector((state: RootState) => state.user.search_string);
  const [searchQuery, onSearchChange] = useState<string|undefined>(search_string);
  const isFirstLoad = useRef(true);

  const search = useCallback(async (str: string|undefined) => {
    if (search_string !== undefined) {
      AppUtilities.info("community-loggedin index ResultsHeader search: " + str);
      try {
        showSpinner();
        const response = await AppUtilities.fetchAuthorized(
          "ResultsHeader.search",
          "/rabbl/api/search",
          "POST",
          {
            domain: domain,
            offset: 0,
            search_string: str,
          }
        );
        dispatch(setData(response));
      } catch (e) {
        AppUtilities.logException(e, "ResultsHeader.search");
      } finally {
        hideSpinner();
      }
    }
  }, [domain]);

  useEffect(() => {
    let shouldSearch = true;
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      if (!search_string || search_string.length == 0) {
        shouldSearch = false;
      }
    }
    if (shouldSearch) {
      search(search_string);
    }
  }, [search_string, search]);

  const debouncedSearch = useCallback(
    debounce((str: string|undefined) => {
      dispatch(setSearchString(str ?? ""));
    }, 300),
    [],
  );

  useEffect(() => {
    debouncedSearch(searchQuery);
  }, [searchQuery]);

  const onUpdateSearch = (str: string) => {
    onSearchChange(str);
  };

  return (
    <View style={{ padding: 10, paddingHorizontal: 20, backgroundColor: "transparent", gap: 10, alignItems: "center" }}>
      <View style={{ flexDirection: "row", alignItems: "center", backgroundColor: "transparent" }}>
        <TextInput value={searchQuery ?? ""} placeholder="Search" onChangeText={onUpdateSearch} style={styles.searchInput} />
        {
          searchQuery && searchQuery.length > 0 ?
              <Pressable style={AppConstants.STYLES.hoveringButtonRight} hitSlop={20} onPress={() => {Keyboard.dismiss(); onUpdateSearch('');}}>
                <CloseIcon />
              </Pressable>
            :
              null
        }
      </View>
      <View style={{ flexDirection: 'row', gap: 10, backgroundColor: 'transparent', alignItems: 'center', justifyContent: 'center', maxWidth: AppConsts.MAX_WIDTH_COMFORTABLE_READING }}>
        <Link href="/filter" asChild={true} className={"nontext"}><Button>Filter:</Button></Link>
        <AppText>{getFilterText(communityMembership)}</AppText>
      </View>
      
    </View>
  );
}

function Results() : React.ReactNode {
  const accumulatedSearchResults = useSelector((state: RootState) => state.user.accumulatedSearchResults);
  const next_search_offset = useSelector((state: RootState) => state.user.next_search_offset);
  const search_string = useSelector((state: RootState) => state.user.search_string);
  const [isProcessingOnEndReached, setIsProcessingOnEndReached] = useState(false);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const domain = useSelector((state: RootState) => state.user.domain);
  const dispatch = useDispatch();
  const { width, height } = useWindowDimensions();
  const [ shouldCheckOnEndReached, setShouldCheckOnEndReached ] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const isMainLogicalHost = useSelector((state: RootState) => state.user.isMainLogicalHost);
  const headerHeight = useHeaderHeight();
  const tabBarHeight = useBottomTabBarHeight();

  const header = useMemo(() => <ResultsHeader />, []);
  const noResults = useMemo(() => <NoResults />, []);
  const separator = useMemo(() => <View style={styles.rowSeparator} />, []);
  const maxCols = Math.floor((width - (AppConstants.BASIC_PADDING_HORIZONTAL * 2)) / (ITEM_WIDTH + (ITEM_HORIZONTAL_PADDING * 2)));
  const imageHeight = Math.floor(
    maxCols == 1 ?
        height - headerHeight - tabBarHeight - NON_IMAGE_HEIGHT - HEADER_HEIGHT
      :
        PROFILE_IMAGE_HEIGHT
  );
  const itemHeight = imageHeight + NON_IMAGE_HEIGHT;
  const imageWidth = maxCols == 1 ? width - 40 : PROFILE_IMAGE_WIDTH;

  if (AppUtilities.DEBUGLEVEL.layout || AppUtilities.DEBUGLEVEL.beta) {
    AppUtilities.debug("(community-loggedin-members-tabs)/index Results render maxCols = " + maxCols + ", imageWidth: " + imageWidth + ", imageHeight: " + imageHeight + ", screenWidth: " + width + ", screenHeight: " + height + ", itemHeight: " + itemHeight + ", NON_IMAGE_HEIGHT: " + NON_IMAGE_HEIGHT);
  }

  useFocusEffect(
    useCallback(() => {
      AppUtilities.debug("Results.useFocusEffect focused");
      setShouldCheckOnEndReached(true);
      return () => {
        AppUtilities.debug("Results.useFocusEffect blurred");
        setShouldCheckOnEndReached(false);
      };
    }, [])
  );

  const onEndReached = useCallback(async () => {
    if (shouldCheckOnEndReached && !isProcessingOnEndReached) {
      try {
        AppUtilities.debug("community-loggedin index onEndReached");
        setIsProcessingOnEndReached(true);
        //showSpinner();
        AppUtilities.debug("Results.onEndReached searching with offset " + next_search_offset);
        const response = await AppUtilities.fetchAuthorized(
          "Results.onEndReached",
          "/rabbl/api/search",
          "POST",
          {
            domain: domain,
            offset: next_search_offset,
            search_string: search_string,
          }
        );
        dispatch(appendSearchResults(response));
      } catch (e) {
        AppUtilities.logException(e, "Results.onEndReached");
      } finally {
        //hideSpinner();
        setIsProcessingOnEndReached(false);
      }
    }
  }, [shouldCheckOnEndReached, isProcessingOnEndReached, next_search_offset, search_string]);

  const onRefresh = useCallback(async () => {
    try {
      setRefreshing(true);
      const response = await AppUtilities.fetchAuthorized(
        "Results.onRefresh",
        "/rabbl/api/initialdata",
        "POST",
        {
          domain: domain,
        }
      );
      dispatch(setData(response));
    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e));
    } finally {
      setRefreshing(false);
    }
  }, [domain, isMainLogicalHost]);

  return (
    <FlatList
      key={"listColumns" + maxCols}
      keyExtractor={(searchResult : CommunityMembership) => searchResult.uniqueid}
      data={accumulatedSearchResults}
      renderItem={({ item }: { item: CommunityMembership }) => {
        return (
          <ItemContainer
            member={item}
            itemHeight={itemHeight}
            imageHeight={imageHeight}
            imageWidth={imageWidth}
            referenceDomain={domain}
            referenceMaxCols={maxCols}
          />
        );
      }}
      style={AppConstants.STYLES.flex1}
      contentContainerStyle={styles.listContentContainerStyle}
      ListHeaderComponent={header}
      ListEmptyComponent={noResults}
      ItemSeparatorComponent={separator}
      getItemLayout={(data, index) => (
        {
          length: itemHeight,
          offset: Math.floor(index / maxCols) * (itemHeight + ROW_SEPARATOR_HEIGHT),
          index,
        }
      )}
      numColumns={maxCols}
      onEndReached={onEndReached}
      refreshing={refreshing}
      refreshControl={<RefreshComponent refreshing={refreshing} onRefresh={onRefresh} />}
    />
  );
}

export default function Index() {
  const community = useSelector((state: RootState) => state.user.current_community);

  if (AppUtilities.DEBUGLEVEL.dev) {
    AppUtilities.info("(community-loggedin-member-alltabs)/ render");
  }

  return (
    <BasicWrapperView center={false} style={AppConstants.STYLES.noHorizontalMargin}>
      <Meta />
      <Results />
    </BasicWrapperView>
  );
}
